import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getNumber,
    getScale,
    getScales,
    getString,
    mapRiskLabel,
    mapScreeningLabel,
    type OutcomeTableConfig,
} from '../helpers';

export const rsesConfig: OutcomeTableConfig = {
    title: 'Resultados Escala de Autoestima de Rosenberg',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Autoestima global (total RSES)',
                result: formatOutOf(total.raw, 40),
                interpretation:
                    'Maior pontuação = maior autoestima (faixas descritivas: 10-25 baixa, 26-35 média, 36-40 alta)',
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        return getNumber(total.min) === 10 && getNumber(total.max) === 40;
    },
};

export const cesdConfig: OutcomeTableConfig = {
    title: 'Resultados CES-D',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Sintomas depressivos (total CES-D)',
                result: formatOutOf(total.raw, 60),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 16)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
};

export const auditConfig: OutcomeTableConfig = {
    title: 'Resultados AUDIT',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Uso de álcool (total AUDIT)',
                result: formatOutOf(total.raw, 40),
                interpretation: getString(total.zone_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 8)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'zone_label' in getScale(scales, 'total');
    },
};

export const srq20Config: OutcomeTableConfig = {
    title: 'Resultados SRQ-20',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const risk = getScale(scales, 'risk');

        return [
            {
                domain: 'Transtorno mental comum (total SRQ-20)',
                result: formatOutOf(total.raw, 20),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Ideação de acabar com a própria vida (item 17)',
                result: formatYesNo(risk.suicidal_ideation_flag),
                interpretation: mapRiskLabel(risk.suicidal_ideation_flag),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'suicidal_ideation_flag' in getScale(scales, 'risk');
    },
};

export const scoffConfig: OutcomeTableConfig = {
    title: 'Resultados SCOFF',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Rastreamento de transtorno alimentar (total SCOFF)',
                result: formatOutOf(total.raw, 5),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
};

export const swlsConfig: OutcomeTableConfig = {
    title: 'Resultados SWLS',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Satisfação com a vida (total SWLS)',
                result: formatOutOf(total.raw, 35),
                interpretation: getString(total.band_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        return 'band_label' in total && 'higher_is_better' in total;
    },
};

export const pswqConfig: OutcomeTableConfig = {
    title: 'Resultados PSWQ',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Preocupação (total PSWQ)',
                result: formatOutOf(total.raw, 80),
                interpretation: getString(total.severity_label),
            },
            {
                domain: 'Rastreio de TAG (ponto de corte >= 45)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
};

export const ftndConfig: OutcomeTableConfig = {
    title: 'Resultados FTND (Fagerström)',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Dependência de nicotina (total FTND)',
                result: formatOutOf(total.raw, 10),
                interpretation: getString(total.dependence_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'dependence_label' in getScale(scales, 'total');
    },
};

export const mhi5Config: OutcomeTableConfig = {
    title: 'Resultados MHI-5',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Índice de saúde mental (0-100)',
                result: formatOutOf(total.index, 100),
                interpretation: 'Maior pontuação = melhor saúde mental',
            },
            {
                domain: 'Rastreio de sofrimento mental (índice <= 52)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'index' in getScale(scales, 'total');
    },
};

export const gds15Config: OutcomeTableConfig = {
    title: 'Resultados GDS-15',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Sintomas depressivos (total GDS-15)',
                result: formatOutOf(total.raw, 15),
                interpretation: getString(total.band_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte 5/6)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        return 'band_label' in total && 'screening_positive' in total;
    },
};

export const pfefferConfig: OutcomeTableConfig = {
    title: 'Resultados Questionário de Pfeffer (FAQ)',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Dependência funcional (total Pfeffer)',
                result: formatOutOf(total.raw, 30),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Respondente (informante)',
                result: getString(outcomes.respondent),
                interpretation: 'Questionário respondido por informante',
            },
        ];
    },
};
